import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { useState } from 'react';
import LambdaIcon from '../../../../assets/aws/lambda.svg';
import {
  LAMBDA_MEMORY_MB_INPUT_PROPS,
  LAMBDA_TIMEOUT_SECONDS_INPUT_PROPS,
} from 'config-editor-common';
import type { ValkyrieLambda } from 'config-editor-common';
import { ValkyrieEnvVarsSection } from './ValkyrieEnvVarsSection';
import { ValkyrieSqsQueueSelector } from './ValkyrieSqsQueueSelector';

// SQS + Lambda combined (queue consumer = SQS pink + Lambda orange)
const SQS_PINK = '#E7157B';
const SQS_PINK_LIGHT = '#F4A8C8';
const LAMBDA_ORANGE = '#FF9900';
const LAMBDA_ORANGE_LIGHT = '#FFB84D';

const useStyles = makeStyles(theme => ({
  lambdaItem: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: '2px solid transparent',
    borderRadius: theme.shape.borderRadius,
    background: `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}) padding-box, linear-gradient(135deg, ${SQS_PINK_LIGHT}, ${LAMBDA_ORANGE_LIGHT}) border-box`,
    boxShadow: theme.shadows[1],
    position: 'relative',
    '&:hover': {
      boxShadow: theme.shadows[2],
      background: `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}) padding-box, linear-gradient(135deg, ${SQS_PINK}, ${LAMBDA_ORANGE}) border-box`,
    },
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  nameContainer: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  lambdaIcon: {
    width: '1.25rem',
    height: '1.25rem',
    flexShrink: 0,
  },
}));

interface ValkyrieQueueConsumerItemEditorProps {
  name: string;
  lambda: ValkyrieLambda;
  onUpdate: (name: string, field: keyof ValkyrieLambda, value: unknown) => void;
  onRename: (oldName: string, newName: string) => void;
  onDelete: (name: string) => void;
  queueNames?: string[];
}

export function ValkyrieQueueConsumerItemEditor({
  name,
  lambda,
  onUpdate,
  onRename,
  onDelete,
  queueNames = [],
}: ValkyrieQueueConsumerItemEditorProps) {
  const classes = useStyles();
  const [nameInput, setNameInput] = useState(name);

  const handleRename = () => {
    if (nameInput.trim() && nameInput !== name) {
      onRename(name, nameInput.trim());
    }
  };

  return (
    <div className={classes.lambdaItem}>
      <div className={classes.headerRow}>
        <div className={classes.nameContainer}>
          <img src={LambdaIcon} alt="Lambda" className={classes.lambdaIcon} />
          <TextField
            label="Lambda Name"
            value={nameInput}
            onChange={e => setNameInput(e.target.value)}
            onBlur={handleRename}
            fullWidth
            variant="outlined"
            size="small"
          />
        </div>
        <Tooltip title="Delete Lambda">
          <IconButton
            size="small"
            onClick={() => onDelete(name)}
            color="secondary"
            tabIndex={-1}
            data-ai-anchor={`lambda.${name}.delete`}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </div>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Lambda Handler"
            value={lambda.lambda_handler || ''}
            onChange={e => onUpdate(name, 'lambda_handler', e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Namespace::Class::Method"
          />
        </Grid>
        {queueNames.length > 0 && (
          <Grid item xs={12} md={6}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Consumes from queue</InputLabel>
              <Select
                value={lambda.sqs_queue || ''}
                onChange={e => onUpdate(name, 'sqs_queue', (e.target.value as string) || '')}
                label="Consumes from queue"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {queueNames.map(q => (
                  <MenuItem key={q} value={q}>
                    {q}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} md={3}>
          <TextField
            label="Timeout (s)"
            type="number"
            value={lambda.timeout ?? 300}
            onChange={e =>
              onUpdate(name, 'timeout', e.target.value ? parseInt(e.target.value, 10) : 300)
            }
            fullWidth
            variant="outlined"
            size="small"
            inputProps={{ ...LAMBDA_TIMEOUT_SECONDS_INPUT_PROPS }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Memory (MB)"
            type="number"
            value={lambda.memory_size ?? 256}
            onChange={e =>
              onUpdate(name, 'memory_size', e.target.value ? parseInt(e.target.value, 10) : 256)
            }
            fullWidth
            variant="outlined"
            size="small"
            inputProps={{ ...LAMBDA_MEMORY_MB_INPUT_PROPS }}
          />
        </Grid>
        {queueNames.length > 0 && (
          <Grid item xs={12} md={6}>
            <ValkyrieSqsQueueSelector
              value={Array.isArray(lambda.sqs_send_policies) ? lambda.sqs_send_policies : []}
              onChange={v => onUpdate(name, 'sqs_send_policies', v)}
              queueNames={queueNames}
              label="SQS Send Policies"
              helperText="Queues this lambda can send messages to"
            />
          </Grid>
        )}
      </Grid>

      <ValkyrieEnvVarsSection
        envVars={lambda.env_vars ?? {}}
        sqsQueueUrls={lambda.sqs_queue_urls ?? {}}
        onEnvVarsChange={vars => onUpdate(name, 'env_vars', vars)}
        onSqsQueueUrlsChange={urls => onUpdate(name, 'sqs_queue_urls', urls)}
        queueNames={queueNames}
      />
    </div>
  );
}
